import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import './YOCILABAcademy.css';
import heroImg from '../../assets/YOCILAB Webiste photos/Academy Home/DSC_4706.jpg';
import programImg1 from '../../assets/YOCILAB Webiste photos/Academy Home/16.png';
import programImg2 from '../../assets/YOCILAB Webiste photos/Academy Home/2.png';
import abetaHeroImg from '../../assets/YOCILAB Webiste photos/Academy Home/ABETA YOCILAB Academy/REMS9766.jpg';
import partnerImg from '../../assets/YOCILAB Webiste photos/Academy Home/678816018_122257444856103169_2951457947465501560_n.jpg';

const focusAreas = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
    title: 'AI, Technology & Digital Transformation',
    stat: '3,680+',
    statLabel: 'youth trained',
    desc: 'We work with schools, educators, technology partners, and youth-serving organizations to expand access to AI and digital skills training. We help young people build, innovate, and participate in the digital economy.',
    color: 'var(--color-academy)',
    bg: 'var(--color-academy-faint)',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
      </svg>
    ),
    title: 'Entrepreneurship & Innovation',
    stat: '500+',
    statLabel: 'entrepreneurs supported',
    desc: 'We help aspiring entrepreneurs and innovators develop the knowledge, capabilities, and mindset to identify opportunities, build solutions, and create sustainable ventures that strengthen local economies.',
    color: 'var(--color-accent-dark)',
    bg: 'var(--color-accent-faint)',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
      </svg>
    ),
    title: 'Career & Workforce Readiness',
    stat: '1,200+',
    statLabel: 'careers launched',
    desc: 'We help young people develop the capabilities they need to build meaningful careers and thrive in a changing world of work — from confidence and adaptability to practical skills and opportunity access.',
    color: 'var(--color-primary)',
    bg: 'var(--color-primary-faint)',
  },
];

const YOCILABAcademy = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    const targets = sectionRef.current?.querySelectorAll('.fade-up, .slide-left, .slide-right');
    targets?.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="academy-page" ref={sectionRef}>
      <SEO
        title="YOCILAB Academy"
        description="Expanding access to skills and knowledge young Africans need to shape the future — AI, entrepreneurship, career readiness and the ABETA Builders Platform."
      />

      {/* ─── HERO ─── */}
      <section className="academy-hero">
        <div className="academy-hero__bg">
          <img src={heroImg} alt="YOCILAB Academy students" className="academy-hero__img" />
          <div className="academy-hero__overlay" />
        </div>
        <div className="container">
          <span className="academy-hero__label">YOCILAB ACADEMY</span>
          <h1 className="academy-hero__title">
            Expanding Access to Skills &amp;<br />
            Knowledge Young Africans Need
          </h1>
          <p className="academy-hero__desc">
            Equipping young Africans with the skills, knowledge, and opportunities to participate in and shape a rapidly changing world.
          </p>
        </div>
      </section>

      {/* ─── IMPACT BAR ─── */}
      <section className="academy-impact-bar">
        <div className="container">
          <div className="academy-impact-bar__grid">
            {[
              { value: '3,680+', label: 'Young people trained in AI & Digital skills' },
              { value: '50+', label: 'Partner schools and organizations' },
              { value: '5', label: 'Regions of Cameroon reached' },
              { value: '3', label: 'Core learning tracks available' },
            ].map((s) => (
              <div key={s.label} className="academy-impact-bar__stat">
                <span className="academy-impact-bar__value">{s.value}</span>
                <span className="academy-impact-bar__label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHAT WE FOCUS ON ─── */}
      <section className="academy-focus">
        <div className="container">
          <div className="academy-focus__header fade-up">
            <span className="section-label">WHAT WE FOCUS ON</span>
            <h2 className="academy-focus__title">Three areas where we build impact</h2>
            <p className="academy-focus__desc">
              Through practical learning, technology-enabled education, partnerships, and programs focused on the future of work, we help young people move from learning to opportunity — and from opportunity to impact.
            </p>
          </div>

          <div className="academy-focus__grid">
            {focusAreas.map((f, i) => (
              <div
                key={f.title}
                className="focus-card fade-up"
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="focus-card__icon" style={{ background: f.bg, color: f.color }}>
                  {f.icon}
                </div>
                <div className="focus-card__stat-row">
                  <span className="focus-card__stat" style={{ color: f.color }}>{f.stat}</span>
                  <span className="focus-card__stat-label">{f.statLabel}</span>
                </div>
                <h3 className="focus-card__title">{f.title}</h3>
                <p className="focus-card__desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MEET ABETA ─── */}
      <section className="academy-abeta">
        <div className="container">
          <div className="academy-abeta__grid">
            <div className="academy-abeta__visual slide-left">
              <div className="academy-abeta__img-wrapper">
                <img src={abetaHeroImg} alt="ABETA Platform" className="academy-abeta__img" loading="lazy" />
                <div className="academy-abeta__img-badge">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                  </svg>
                  AI-Powered Platform
                </div>
              </div>
            </div>

            <div className="academy-abeta__content slide-right">
              <span className="section-label" style={{ color: 'var(--color-abeta)' }}>THE BUILDERS PLATFORM</span>
              <h2 className="academy-abeta__title">Meet ABETA</h2>
              <p className="academy-abeta__subtitle">Your learning journey, built around you.</p>
              <p className="academy-abeta__body">
                ABETA is YOCILAB Academy's AI-powered Builders Platform, helping young people discover their strengths, build future-ready skills, create real-world experience, and connect to opportunities. From your first learning experience to your next career, venture, or impact opportunity, ABETA brings the journey together in one place.
              </p>

              <div className="academy-abeta__journey">
                {['DISCOVER', 'LEARN', 'BUILD', 'CONNECT', 'PASSPORT'].map((step, i, arr) => (
                  <React.Fragment key={step}>
                    <span className="academy-abeta__step">{step}</span>
                    {i < arr.length - 1 && (
                      <span className="academy-abeta__step-arrow">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <Link to="/programs/abeta" className="academy-abeta__btn">
                Learn more about ABETA
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROGRAMS ─── */}
      <section className="academy-programs">
        <div className="container">
          <div className="academy-programs__intro fade-up">
            <span className="section-label">OUR PROGRAMS</span>
            <h2 className="academy-programs__title">Beyond the platform</h2>
            <p className="academy-programs__desc">
              YOCILAB Academy works with schools, communities, organizations, and partners to deliver learning programs that respond to emerging needs. Our programs focus on:
            </p>
            <ul className="academy-programs__list">
              {['AI and digital skills', 'Entrepreneurship and innovation', 'Career and workforce readiness', 'Future of work education', 'Practical technology education', 'Skills for emerging economies'].map(item => (
                <li key={item} className="academy-programs__list-item">
                  <span className="academy-programs__check">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="academy-programs__imgs">
            <img src={programImg1} alt="Academy programs" className="academy-programs__img fade-up" loading="lazy" />
            <img src={programImg2} alt="YOCILAB Academy learning" className="academy-programs__img fade-up" style={{ transitionDelay: '120ms' }} loading="lazy" />
          </div>
        </div>
      </section>

      {/* ─── PARTNERS ─── */}
      <section className="academy-partners">
        <div className="container">
          <div className="academy-partners__grid">
            <div className="academy-partner-block fade-up">
              <div className="academy-partner-block__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h3 className="academy-partner-block__title">For Schools &amp; Educators</h3>
              <p className="academy-partner-block__body">
                We work with schools and educators to introduce young people to technology, entrepreneurship, career development, and the changing world of work. Together, we can create learning experiences that complement formal education and prepare students for the opportunities ahead.
              </p>
              <Link to="/contact" className="academy-partner-block__link">
                Partner With Us →
              </Link>
            </div>

            <div className="academy-partner-block fade-up" style={{ transitionDelay: '150ms' }}>
              <div className="academy-partner-block__img-wrapper">
                <img src={partnerImg} alt="YOCILAB partners" className="academy-partner-block__img" loading="lazy" />
              </div>
            </div>

            <div className="academy-partner-block fade-up" style={{ transitionDelay: '250ms' }}>
              <div className="academy-partner-block__icon" style={{ background: 'var(--color-academy-faint)', color: 'var(--color-academy)' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              </div>
              <h3 className="academy-partner-block__title">For Organizations &amp; Partners</h3>
              <p className="academy-partner-block__body">
                We collaborate with foundations, companies, educational institutions, governments, technology organizations, and youth-serving organizations to expand access to relevant skills and learning opportunities. Your support can help us reach more young people and build pathways from learning to opportunity.
              </p>
              <Link to="/contact" className="academy-partner-block__link">
                Work With YOCILAB →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── JOIN NOW, PAY LATER ─── */}
      <section className="academy-join">
        <div className="container">
          <div className="academy-join__card fade-up">
            <div className="academy-join__content">
              <span className="section-label section-label--accent">JOIN THE COMMUNITY</span>
              <h2 className="academy-join__title">Can't pay the registration fee right now? Join anyway.</h2>
              <p className="academy-join__desc">
                Money should never be the reason a young person misses out on learning. You can join the YOCILAB community today, completely free, and settle your program registration fee whenever you're able to — no deadline, no pressure.
              </p>
              <ul className="academy-join__list">
                <li>
                  <span className="academy-join__check">✓</span>
                  Join the WhatsApp community instantly — free, no payment required
                </li>
                <li>
                  <span className="academy-join__check">✓</span>
                  Stay connected to updates, mentors, opportunities, and fellow builders
                </li>
                <li>
                  <span className="academy-join__check">✓</span>
                  Pay your program registration fee later, whenever you're ready
                </li>
              </ul>
              <div className="academy-join__btns">
                <a
                  href="https://chat.whatsapp.com/GoTOvyXjhjk0Nln2LPfsjm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="academy-join__btn academy-join__btn--primary"
                >
                  Join Our WhatsApp Community
                </a>
                <Link to="/contact" className="academy-join__btn academy-join__btn--outline">
                  Ask About Registration
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="academy-cta">
        <div className="container">
          <div className="academy-cta__content fade-up">
            <h2 className="academy-cta__title">
              Your Future Starts with<br />What You Learn Today.
            </h2>
            <p className="academy-cta__desc">
              Whether you're exploring technology, thinking about starting a business, preparing for your first job, or simply looking to develop a new skill — there's a place for you at YOCILAB Academy.
            </p>
            <div className="academy-cta__btns">
              <Link to="/programs/abeta" className="academy-cta__btn academy-cta__btn--primary">
                Start Your Journey
              </Link>
              <Link to="/contact" className="academy-cta__btn academy-cta__btn--outline">
                Sponsor a Youth
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YOCILABAcademy;